import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import LoadingOverlay from './LoadingOverlay';
import {
  Animator,
  FilteredGraphWrapper,
  FolderNodeConverter,
  FoldingManager,
  FoldingSynchronizationOptions,
  Font,
  Graph,
  GraphComponent,
  GraphItemTypes,
  GraphViewerInputMode,
  GroupingSupport,
  GroupNodeStyle,
  HierarchicalNestingPolicy,
  IEdge,
  IFoldingView,
  ILabel,
  IModelItem,
  INode,
  InputModeItemEventArgs,
  Insets,
  ITable,
  LabelStyle,
  Matrix,
  MatrixOrder,
  MutableRectangle,
  NavigationInputMode,
  NodeAlignmentPolicy,
  NodeStyleLabelStyleAdapter,
  NodeStyleStripeStyleAdapter,
  PointerButtons,
  Rect,
  RectangleNodeStyle,
  ShapeNodeStyle,
  Table,
  WebGLGraphModelManager
} from '@yfiles/yfiles';

import './lib/yFilesLicense';
import { setupHoverHighlighting } from './lib/hoverHighlighting';
import { getFlowItems, getFlowNodes, getProductsForNode } from './lib/graphUtils';
import { useFoldingState } from './lib/useFoldingState';

import loadGraph from './lib/loadGraph';
import { calculateDynamicHeight } from './lib/nodeCreators';
// import apiData from './assets/SampleVS.json';
// import { transformApiToGraphData } from './lib/transformApiToAppData';
import {
  alarmHaloLabelLayoutParameter,
  alarmLabelLayoutParameter,
  columnHeaderStyle,
  getAnimatedProductEdgeStyle,
  getLeafNodeStyle,
  getProductEdgeStyle,
  searchNodeStyleIndicatorRenderer
} from './lib/styles';
import {
  alarmIconLabelStyle,
  getApplicationGroupNodeStyle,
  getCapabilityGroupNodeStyle,
  getTeamGroupNodeStyle,
  groupNodePercentageLabelLayoutParameter,
  groupNodePercentageLabelStyle,
  HeatMapColor
} from './lib/groupNodeStyles';
import { inclination2D, inclination3D, rotation2D, rotation3D } from './lib/defaults';
import { IsometricNodeComparator } from './lib/IsometricNodeComparator';
import { ProjectionAnimation } from './lib/ProjectionAnimation';
import { ValueStreamGraphModelManager } from './lib/ValueStreamGraphModelManager';
import { runLayout } from './lib/layout';
import { GraphData, PercentageMetrics, Product } from './lib/types';

import '@yfiles/yfiles/yfiles.css';

import './style.css';
import './tooltips.css';
import './assets/icons/icons.css';
import Toolbar from './components/Toolbar';
import ProductFilter from './components/ProductFilter';
import ProgressiveCollapse, {
  ProgressiveCollapseHandle
} from './components/ProgressiveCollapse/ProgressiveCollapse';
import { updatePortVisuals } from './lib/portVisuals';
import { installWorldBackground } from './lib/WorldBackground';
import { cn } from './utils';
import { Activity } from 'lucide-react';
// Import the calculation function and adapter
import { calculateGroupNodePercentages } from './lib/calculateGroupNodePercentages';
import { YFilesGraphAdapter } from './lib/yFilesGraphAdapter';
import { compressStringArray, decompressStringArray } from './lib/urlCompression';
import { unwrap3DStyle, wrapInto3DStyle } from './lib/util.ts';
import { isSupportedStyle } from './lib/Pseudo3DNodeStyle.ts';
import { Pseudo3DLabelStyle } from './lib/Pseudo3DLabelStyle.ts';

interface OrbusStreamNavigatorProps {
  insetLeft?: number;
  insetTop?: number;
  insetRight?: number;
  insetBottom?: number;
  graphData: GraphData;
  onItemSingleClick?: (item: IModelItem | null, graphComponent: GraphComponent) => void;
  portalTargetId?: string;
  onSearchApiReady?: (api: {
    search: (term: string) => void;
    zoomStageHeader: (stageName: string) => void;
  }) => void;
  onHealthSnapshot?: (payload: {
    stages: Array<{ id: string; name: string; health: number }>;
    capabilities: Array<{ id: string; name: string; health: number }>;
    timestamp: number;
  }) => void;
  onPerformanceMetricChange?: (metric: string) => void;
  renderer?: 'webgl' | 'svg';
}

// Debug counter for layout operations
let layoutRunCounter = 0;

// Helper to log layout operations with source information
const logLayoutOperation = (source: string, details: string = '') => {
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0]; // HH:MM:SS
  layoutRunCounter++;
  console.debug(
    `[LAYOUT ${layoutRunCounter}] ${timestamp} - ${source}${details ? ': ' + details : ''}`
  );
};

/**
 * OrbusStreamNavigator component with URL state sharing
 *
 * State is synchronized with URL search parameters:
 * - `performanceMetric`: string - controls the selected performance metric (Health, Time, Volume, Quality, Sentiment, Cost, Risk)
 * - `products`: string - gzip-compressed and base64url-encoded array of visible product names
 *
 * Example URL: ?collapseLevel=2&performanceMetric=Time&products=gz.H4sIAAAAAAAA...
 *
 * The state can be shared by copying the URL, and the component will restore
 * the performance metric and visible products when loaded with those parameters.
 */

const OrbusStreamNavigator: React.FC<OrbusStreamNavigatorProps> = ({
  insetLeft = 100,
  insetTop = 100,
  insetRight = 100,
  insetBottom = 100,
  graphData,
  onItemSingleClick,
  portalTargetId,
  onSearchApiReady,
  onHealthSnapshot,
  onPerformanceMetricChange,
  renderer = 'svg'
}) => {
  const graphComponentRef = useRef<HTMLDivElement>(null);
  const [graphComponentInstance, setGraphComponentInstance] = useState<GraphComponent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [products, setProducts] = useState<Product[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [highlightedNodes, setHighlightedNodes] = useState<INode[]>([]);
  const [shownProducts, setShownProducts] = useState<Set<string>>(new Set());
  const prevShownProductsRef = useRef<Set<string>>(new Set());
  const [flowNodesToFilterFor, setFlowNodesToFilterFor] = useState<INode[]>([]);
  const [flowEdgesToFilterFor, setFlowEdgesToFilterFor] = useState<IEdge[]>([]);
  const [viewType, setViewType] = useState<'FLOW' | 'TABULAR'>('TABULAR');
  const prevViewTypeRef = useRef<'FLOW' | 'TABULAR'>('TABULAR');
  const [foldingView, setFoldingView] = useState<any>(null);
  const [fullGraph] = useState<Graph>(new Graph());
  const [isometricNodeComparator, setIsometricNodeComparator] =
    useState<IsometricNodeComparator | null>(null);
  const [viewDimension, setViewDimension] = useState<'2D' | '3D'>('2D');
  const [orientation, setOrientation] = useState<{ rotation: number; inclination: number }>({
    rotation: rotation3D,
    inclination: inclination3D
  });
  const layoutTimeoutRef = useRef<number | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);

  // URL state management for performance metric
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMetricInitialized, setIsMetricInitialized] = useState<boolean>(false);
  const [isProductsInitialized, setIsProductsInitialized] = useState<boolean>(false);

  const getInitialPerformanceMetric = () => {
    const param = searchParams.get('performanceMetric');
    const validMetrics = ['Health', 'Time', 'Volume', 'Quality', 'Sentiment', 'Cost', 'Risk'];
    const result = param && validMetrics.includes(param) ? param : 'Health';
    return result;
  };

  const [selectedPerformanceMetric, setSelectedPerformanceMetric] = useState<string>(
    getInitialPerformanceMetric
  );
  const divElementRef = useRef<HTMLElement | null>(null);
  const progressiveCollapseRef = useRef<ProgressiveCollapseHandle>(null);
  const expandedNodesRef = useRef<Set<string>>(new Set());
  const animatorRef = useRef<Animator | null>(null);
  const dotPixelsPerSecondRef = useRef<number>(300);
  const portalTargetEl: Element | null = (() => {
    if (typeof document === 'undefined') return null;
    if (portalTargetId) {
      const el = document.getElementById(portalTargetId);
      if (el) return el;
    }
    return document.body;
  })();

  const { captureExpandedState, restoreExpandedState } = useFoldingState(foldingView);

  // Configure hover highlighting when graph component or view type changes
  useEffect(() => {
    if (!graphComponentInstance) return;
    return setupHoverHighlighting(graphComponentInstance, viewType);
  }, [graphComponentInstance, viewType]);

  // Configure search highlight style when the graph component is changed
  useEffect(() => {
    if (!graphComponentInstance) return;

    // Custom highlight visual for the search results
    graphComponentInstance.graph.decorator.nodes.highlightRenderer.addConstant(
      searchNodeStyleIndicatorRenderer
    );
  }, [graphComponentInstance, viewType]);

  const nodePredicate = useCallback(
    (node: INode): boolean => {
      if (isLoading) {
        return true;
      }
      if (ITable.getTable(node)) {
        return true;
      }

      if (flowNodesToFilterFor.length > 0) {
        return flowNodesToFilterFor.includes(node);
      }

      if (!fullGraph.isGroupNode(node)) {
        if (node.tag?.type === 'PRODUCT') {
          return shownProducts.has(node.tag.Name);
        }
        // For leaf nodes: only show if they have edges with products in shownProducts
        if (node.tag?.type === 'LEAF') {
          return fullGraph
            .edgesAt(node)
            .some((edge) => edge.tag && edge.tag.product && shownProducts.has(edge.tag.product));
        }
        // For other non-group nodes, check connections
        return fullGraph
          .edgesAt(node)
          .some((edge) => edge.tag && edge.tag.product && shownProducts.has(edge.tag.product));
      }

      // For group nodes: if filtering is active, only show if at least one child is visible
      if (shownProducts.size < products.length || flowNodesToFilterFor.length > 0) {
        const children = fullGraph.getChildren(node);
        return Array.from(children).some((child) => nodePredicate(child));
      }
      // If no filtering is active, always show group nodes (even if empty)
      return true;
    },
    [flowNodesToFilterFor, isLoading, fullGraph, shownProducts, products]
  );

  const edgePredicate = useCallback(
    (edge: IEdge): boolean => {
      if (viewType === 'TABULAR') {
        return false;
      }
      if (isLoading) {
        return true;
      }
      if (flowEdgesToFilterFor.length > 0) {
        return flowEdgesToFilterFor.includes(edge);
      }
      return edge.tag && edge.tag.product && shownProducts.has(edge.tag.product);
    },
    [isLoading, flowEdgesToFilterFor, shownProducts, viewType]
  );

  // Function to enable filtering and folding
  const enableFilteringAndFolding = React.useCallback((): IFoldingView => {
    const filteredGraph = new FilteredGraphWrapper(fullGraph, nodePredicate, edgePredicate);

    const manager = new FoldingManager(filteredGraph);

    const folderNodeConverter = new FolderNodeConverter();
    folderNodeConverter.folderNodeDefaults.size = [300, 100];
    folderNodeConverter.folderNodeDefaults.copyLabels = true;
    folderNodeConverter.folderNodeDefaults.labels.updateFoldingOptions =
      FoldingSynchronizationOptions.ALL;
    folderNodeConverter.folderNodeDefaults.labels.initializeOptions =
      FoldingSynchronizationOptions.ALL;
    manager.folderNodeConverter = folderNodeConverter;

    return manager.createFoldingView();
  }, [fullGraph]);

  function fitGraphBounds() {
    if (graphComponentInstance) {
      graphComponentInstance.updateContentBounds();
      const contentBounds = graphComponentInstance.contentBounds;
      const contentWidth = contentBounds.width;
      const contentHeight = contentBounds.height;

      const insets = graphComponentInstance.contentMargins;
      const viewWidth = graphComponentInstance.size.width - insets.horizontalInsets;
      const viewHeight = graphComponentInstance.size.height - insets.verticalInsets;

      const ratioView = viewWidth / viewHeight;
      const ratioContent = contentWidth / contentHeight;

      if (ratioView > ratioContent) {
        return graphComponentInstance.fitGraphBounds({
          animated: true
        });
      } else {
        const height = (viewHeight * contentWidth) / viewWidth;
        const targetZoom = viewWidth / contentWidth;

        return graphComponentInstance.zoomToAnimated(
          new Rect(
            contentBounds.topLeft.x,
            contentBounds.topLeft.y,
            contentWidth,
            height
          ).getEnlarged(
            new Insets(
              insets.top / targetZoom,
              insets.right / targetZoom,
              insets.bottom / targetZoom,
              insets.left / targetZoom
            )
          )
        );
      }
    }
  }

  useEffect(() => {
    const container = graphComponentRef.current;
    if (!container) return;

    const cleanup = () => {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      setGraphComponentInstance(null);
      setFoldingView(null);
    };

    cleanup();

    let gc: GraphComponent;
    try {
      gc = new GraphComponent(container);
      gc.contentMargins = new Insets(insetTop, insetRight, insetBottom, insetLeft);
      setGraphComponentInstance(gc);

      const useWebGL = renderer === 'webgl';
      if (useWebGL) {
        gc.graphModelManager = new WebGLGraphModelManager();
      } else {
        const gmm = new ValueStreamGraphModelManager(gc);
        gc.graphModelManager = gmm;
        // gmm.portGroup.above(gc.renderTree.contentGroup);

        // Install world-scaled dotted background that pans/zooms with the graph
        installWorldBackground(gc);

      const comparator = new IsometricNodeComparator();
      setIsometricNodeComparator(comparator);

        gmm.nodeManager.comparator = comparator.compare.bind(comparator);
      }
      gc.graphModelManager.hierarchicalNestingPolicy = HierarchicalNestingPolicy.GROUP_NODES;

      const inputMode = new GraphViewerInputMode({
        clickableItems: GraphItemTypes.NODE | GraphItemTypes.EDGE,
        selectableItems: GraphItemTypes.NONE,
        focusableItems: GraphItemTypes.NODE
      });

      inputMode.toolTipItems = GraphItemTypes.LABEL;
      inputMode.addEventListener('query-item-tool-tip', (eventArgs) => {
        if (eventArgs.handled) {
          return;
        }
        const hitLabel = eventArgs.item ? (eventArgs.item as ILabel) : null;
        if (hitLabel && hitLabel.tag && hitLabel.tag.tooltip) {
          eventArgs.toolTip = hitLabel.tag.tooltip;
        }
      });

      inputMode.clickInputMode.addEventListener('clicked', (evt) => {
        if ((evt as any).clickCount === 2) {
          handleItemDoubleClick(evt, gc);
        } else if ((evt as any).clickCount === 1) {
          handleItemSingleClick(evt, gc);
        }
      });

      const navigationInputMode = new NavigationInputMode();
      navigationInputMode.allowCollapseGroup = true;
      navigationInputMode.autoGroupNodeAlignmentPolicy = NodeAlignmentPolicy.CENTER;

      inputMode.navigationInputMode = navigationInputMode;

      gc.inputMode = inputMode;

      divElementRef.current = container;

      const initGraph = async () => {
        setIsLoading(true);
        try {
          // Create animator for edge animations
          const animator = new Animator(gc);
          animatorRef.current = animator;
          // Expose dot animation speed (pixels per second). Higher is faster.
          const dotPixelsPerSecond = 300; // adjust as needed or wire to UI state
          dotPixelsPerSecondRef.current = dotPixelsPerSecond;
          const { stages, products } = loadGraph(
            fullGraph,
            graphData,
            animator,
            dotPixelsPerSecond
          );
          setStages(stages);

          const view = enableFilteringAndFolding();
          setFoldingView(view);

          gc.graph = view.graph;

          // Check if we have products in URL
          const productsParam = searchParams.get('products');
          let productSet: Set<string>;

          if (productsParam && !isProductsInitialized) {
            // Restore from URL (async)
            const urlProducts = await decompressStringArray(productsParam);
            const validProducts = urlProducts.filter((name: string) =>
              products.some((p) => p.Name === name)
            );
            productSet =
              validProducts.length > 0
                ? new Set(validProducts)
                : new Set(products.map((p) => p.Name));
            setIsProductsInitialized(true);
          } else {
            // Default: show all products
            productSet = new Set<string>();
            products.forEach((product) => productSet.add(product.Name));
            if (!isProductsInitialized) {
              setIsProductsInitialized(true);
            }
          }

          setShownProducts(productSet);
          prevShownProductsRef.current = new Set(productSet);

          setProducts(products);

          const groupingSupport = new GroupingSupport(gc.graph);
          const descendantsBottomUp = groupingSupport.getDescendantsBottomUp(null);
          descendantsBottomUp.forEach((node) => {
            if (
              ITable.getTable(node) == null &&
              node.tag &&
              node.tag.type !== 'LEAF' &&
              node.tag.type !== 'COLUMN_GROUP'
            ) {
              view.collapse(node);
            }
          });

          // Calculate percentages for all group nodes from their children
          // This must be done before layout and styling
          calculateGroupNodePercentages(new YFilesGraphAdapter(fullGraph));

          // Update the GraphData stages with calculated percentages from the graph nodes
          // This ensures the ValueStreamPerformanceChart shows the correct calculated values
          for (const node of fullGraph.nodes) {
            if (node.tag && node.tag.type === 'COLUMN_GROUP' && node.tag.percentage) {
              const stageName = node.tag.stage;
              const stageData = graphData.stages.find((s) => s.Name === stageName);
              if (stageData) {
                stageData.PercentageMetrics = node.tag.percentage;
              }
            }
          }

          if (typeof onHealthSnapshot === 'function') {
            const stageItems: Array<{ id: string; name: string; health: number }> = [];
            const capabilityItems: Array<{ id: string; name: string; health: number }> = [];
            for (const node of fullGraph.nodes) {
              if (node.tag && node.tag.percentage) {
                if (node.tag.type === 'COLUMN_GROUP') {
                  const stageName = node.tag.stage;
                  stageItems.push({
                    id: stageName,
                    name: stageName,
                    health: node.tag.percentage.HealthPercentage
                  });
                } else if (node.tag.type === 'CAPABILITY') {
                  const capName = node.tag.Name || node.tag.name || 'Capability';
                  const stageName = node.tag.stage || '';
                  capabilityItems.push({
                    id: `${stageName}:${capName}`,
                    name: capName,
                    health: node.tag.percentage.HealthPercentage
                  });
                }
              }
            }
            onHealthSnapshot({
              stages: stageItems,
              capabilities: capabilityItems,
              timestamp: Date.now()
            });
          }

          logLayoutOperation('initGraph', 'initial layout');
          await runLayout(gc, stages, false, 'TABULAR');
          if (gc.graphModelManager instanceof ValueStreamGraphModelManager) {
            gc.graphModelManager.addBackgroundVisuals();
          }

          updateNodeStyles(selectedPerformanceMetric);

          await fitGraphBounds();
        } catch (error) {
          console.error('Error initializing graph:', error);
        } finally {
          setIsLoading(false);
        }
      };

      void initGraph();
    } catch (error) {
      console.error('Error creating GraphComponent:', error);
      setIsLoading(false);
      cleanup();
    }

    return cleanup;
  }, [fullGraph]);

  useEffect(() => {
    if (!isLoading && graphComponentInstance) {
      void fitGraphBounds();
    }
  }, [
    isLoading,
    graphComponentInstance,
    isFilterActive,
    insetLeft,
    insetTop,
    insetRight,
    insetBottom
  ]);

  useEffect(() => {
    if (
      isFilterActive &&
      foldingView &&
      expandedNodesRef.current &&
      expandedNodesRef.current.size > 0
    ) {
      restoreExpandedState(expandedNodesRef.current, foldingView);
    }
  }, [isFilterActive, foldingView, restoreExpandedState]);

  const updateNodeZOrder = useCallback(() => {
    if (!graphComponentInstance || !isometricNodeComparator) return;
    const gmm = graphComponentInstance.graphModelManager;
    if (!(gmm instanceof ValueStreamGraphModelManager)) return;
    isometricNodeComparator.update(graphComponentInstance.projection);
    const nodeManager = gmm.nodeManager;
    for (const node of graphComponentInstance.graph.nodes) {
      nodeManager.update(node);
    }
  }, [graphComponentInstance, isometricNodeComparator]);

  const animateProjection = useCallback(
    async (
      fromRotation: number,
      toRotation: number,
      fromInclination: number,
      toInclination: number,
      direction: 1 | -1
    ) => {
      if (!graphComponentInstance || !isometricNodeComparator) return;

      const projectionAnimation = new ProjectionAnimation(
        graphComponentInstance,
        fromRotation,
        toRotation,
        fromInclination,
        toInclination,
        updateNodeZOrder,
        direction
      );
      const animator = new Animator(graphComponentInstance);
      await animator.animate(projectionAnimation);
    },
    [graphComponentInstance, isometricNodeComparator, updateNodeZOrder]
  );

  // Helper function to calculate heatmap color from percentage
  const getHeatMapColorFromPercentage = useCallback((percentage: number): HeatMapColor | null => {
    if (percentage === 0) return null; // No data
    if (percentage >= 90) return 'GOOD';
    if (percentage >= 75) return 'WARNING';
    return 'BAD';
  }, []);

  // Helper function to get label fill color based on status
  const getLabelFillColor = useCallback((status: HeatMapColor): string => {
    const heatMapLabelFill = {
      GOOD: '#0F7F02',
      WARNING: '#f59e0b',
      BAD: '#ef4444'
    };
    return heatMapLabelFill[status];
  }, []);

  // Update percentage label text and color
  // For group nodes: text is white, background changes based on status
  // For leaf nodes: text is colored based on status
  const updatePercentageLabel = useCallback(
    (
      graph: any,
      label: ILabel,
      displayPercentage: number,
      status: HeatMapColor | null,
      isLeafNode: boolean = false,
      hasTimeseriesData: boolean = true,
      hasCategoryData: boolean = true
    ) => {
      graph.setLabelText(label, displayPercentage + '%');
      const isWrapped = label.style instanceof Pseudo3DLabelStyle;
      const baseStyle = isWrapped
        ? (label.style as Pseudo3DLabelStyle).wrappedLabelStyle
        : label.style;
      const style = (baseStyle as any).clone();
    if (status === null) {
      // No data - use default styling
        if (isLeafNode) {
        style.textFill = '#605e5c'; // Default gray text for leaf nodes
      } else {
        style.textFill = 'white';
        style.backgroundFill = '#605e5c'; // Default gray background for group nodes
      }
    } else if (isLeafNode) {
        // For leaf nodes, update text color
        style.textFill = getLabelFillColor(status);
      } else if (!hasTimeseriesData || !hasCategoryData) {
        // No timeseries data or no metrics for this category — keep grey background
        style.textFill = 'white';
        style.backgroundFill = '#605e5c';
      } else {
        // For group nodes, keep text white but update background color
        style.textFill = 'white';
        style.backgroundFill = getLabelFillColor(status);
      }
      graph.setStyle(label, isWrapped ? new Pseudo3DLabelStyle(style) : style);
    },
    [getLabelFillColor]
  );

  // Remove alarm labels from a node
  const removeAlarmLabels = useCallback((graph: any, alarmLabels: ILabel[]) => {
    for (const label of alarmLabels) {
      graph.remove(label);
    }
  }, []);

  // Add alarm labels to a node
  const addAlarmLabels = useCallback((graph: any, node: INode) => {
    const hasAlarmIcon = node.labels.some((l) => l.tag?.type === 'ALARM_ICON');
    if (hasAlarmIcon) return;

    const iconLabel = graph.addLabel({
      owner: node,
      text: '',
      style: alarmIconLabelStyle,
      layoutParameter: alarmLabelLayoutParameter,
      tag: { type: 'ALARM_ICON' }
    });

    const haloStyle = alarmIconLabelStyle.clone();
    haloStyle.cssClass = 'alarm-icon-halo';

    const haloLabel = graph.addLabel({
      owner: node,
      text: '',
      style: haloStyle,
      layoutParameter: alarmLabelLayoutParameter,
      tag: { type: 'ALARM_HALO' }
    });

    // When the user is currently in 3D view, new labels must be wrapped in
    // the Pseudo3DLabelStyle, otherwise they render at the node's 2D position
    // (next to the projected node) and visually look like an unprojected
    // duplicate of the pulsating label.
    if (viewDimension === '3D') {
      wrapInto3DStyle(graph, iconLabel);
      wrapInto3DStyle(graph, haloLabel);
    }
  }, [viewDimension]);

  // Update leaf node based on performance metric
  const updateLeafNode = useCallback(
    (graph: any, node: INode, performanceMetric: string) => {
      const percentageObj = node.tag.percentage;
      const displayPercentage = percentageObj.getPercentageByMetric(performanceMetric);

      // Update 3D height
      const calculatedHeight = calculateDynamicHeight(displayPercentage);
      node.tag.height = calculatedHeight;

      // Determine status
      const status = getHeatMapColorFromPercentage(displayPercentage);
      const shouldShowAlarm = status === 'BAD';

      // Track alarm labels to remove
      const alarmLabelsToRemove: ILabel[] = [];

    // Update labels
    for (const label of node.labels) {
      if (label.text && label.text.includes('%') && !label.tag?.type) {
        updatePercentageLabel(graph, label, displayPercentage, status, true);
      } else if (label.tag?.type === 'ALARM_ICON' || label.tag?.type === 'ALARM_HALO') {
        if (!shouldShowAlarm || status === null) {
          alarmLabelsToRemove.push(label);
        }
      }
    }

    // Remove or add alarm labels based on status
    removeAlarmLabels(graph, alarmLabelsToRemove);
    if (shouldShowAlarm && status !== null) {
      addAlarmLabels(graph, node);
    }

    // Update node style in 2D mode - only apply heatmapping if status is not null
    if (viewDimension === '2D') {
      graph.setStyle(node, getLeafNodeStyle(status === null ? undefined : displayPercentage, performanceMetric));
    }
  }, [getHeatMapColorFromPercentage, updatePercentageLabel, removeAlarmLabels, addAlarmLabels, viewDimension]);

  // Update group node based on performance metric
  const updateGroupNode = useCallback(
    (graph: any, node: INode, performanceMetric: string) => {
      const percentageObj = node.tag.percentage;
      if (!percentageObj) return;

      const hasTimeseries = percentageObj.hasTimeseriesData !== false;
      const hasCategoryData = percentageObj.hasCategoryMetrics(performanceMetric);
      const displayPercentage = percentageObj.getPercentageByMetric(performanceMetric);
      const heatMapColor = getHeatMapColorFromPercentage(displayPercentage);
      const shouldShowAlarm = hasTimeseries && hasCategoryData && heatMapColor === 'BAD';

      // Dynamic 3D height only when this group is the lowest visible level
      // (i.e. collapsed, so its children are not in the view graph).
      const isLowestVisible = graph.getChildren(node).size === 0;
      if (isLowestVisible && displayPercentage != null) {
        node.tag.height = calculateDynamicHeight(displayPercentage);
      } else {
        node.tag.height = undefined;
      }

    // Update node style - only apply heatmapping if heatMapColor is not null
    if (node.tag.type === 'APPLICATION_GROUP') {
      graph.setStyle(node, getApplicationGroupNodeStyle(heatMapColor || 'NONE', performanceMetric));
    } else if (node.tag.type === 'TEAM') {
      graph.setStyle(node, getTeamGroupNodeStyle(heatMapColor || 'NONE', performanceMetric));
    } else if (node.tag.type === 'CAPABILITY') {
      graph.setStyle(node, getCapabilityGroupNodeStyle(heatMapColor || 'NONE', performanceMetric));
    }

      // Update percentage labels and track underneath labels
      const labelsToRemove: ILabel[] = [];
      let hasUnderneathLabel = false;
      let mainPercentageLabel: ILabel | null = null;

      for (const label of node.labels) {
        // Check if this is an underneath label (handle both raw and 3D-wrapped styles)
        const effectiveStyle = label.style instanceof Pseudo3DLabelStyle ? label.style.wrappedLabelStyle : label.style;
        const isUnderneathLabel =
          effectiveStyle && (effectiveStyle as any).cssClass === 'group-node-style-percentage-underneath';

      // Update percentage label text and color
      if (label.text && label.text.includes('%') && !label.tag?.type) {
        if (isUnderneathLabel) {
          hasUnderneathLabel = true;
          if (!shouldShowAlarm || heatMapColor === null) {
            // Remove underneath label if not BAD or no data for this category
            labelsToRemove.push(label);
          } else {
            // Update underneath label text
            graph.setLabelText(label, displayPercentage + '%');
          }
        } else {
          // Update main percentage label
          updatePercentageLabel(graph, label, displayPercentage, heatMapColor, false, hasTimeseries, hasCategoryData);
          mainPercentageLabel = label;
        }
      }
      // Handle alarm labels
      else if (label.tag?.type === 'ALARM_ICON' || label.tag?.type === 'ALARM_HALO') {
        if (!shouldShowAlarm || heatMapColor === null) {
          labelsToRemove.push(label);
        }
      }
    }

      // Remove labels that should not be shown
      removeAlarmLabels(graph, labelsToRemove);

    // Add underneath label if BAD with data for this category and doesn't exist
    if (shouldShowAlarm && !hasUnderneathLabel && heatMapColor !== null) {
      const underneathStyle = groupNodePercentageLabelStyle.clone();
      underneathStyle.cssClass = 'group-node-style-percentage-underneath';
      underneathStyle.backgroundFill = getLabelFillColor(heatMapColor);
      const newLabel = graph.addLabel({
        owner: node,
        text: displayPercentage + '%',
        style: underneathStyle,
        layoutParameter: groupNodePercentageLabelLayoutParameter
      });
      if (viewDimension === '3D') {
        wrapInto3DStyle(graph, newLabel);
      }

      // The underneath label was appended after the main label, reversing the
      // correct creation-time order (underneath first, main on top).
      // Re-append the main label so it is last in node.labels and painted on top.
      if (mainPercentageLabel !== null) {
        const mainText = mainPercentageLabel.text;
        const mainStyle = mainPercentageLabel.style;
        const mainLayout = mainPercentageLabel.layoutParameter;
        graph.remove(mainPercentageLabel);
        graph.addLabel({
          owner: node,
          text: mainText,
          style: mainStyle,
          layoutParameter: mainLayout
        });
      }
    }

      // Add alarm labels if status is BAD and they don't exist
      // if (shouldShowAlarm) {
      //   addAlarmLabels(graph, node);
      // }
      if (viewDimension === '3D' && isSupportedStyle(node)) {
        wrapInto3DStyle(graph, node);
      }
    },
    [
      getHeatMapColorFromPercentage,
      updatePercentageLabel,
      removeAlarmLabels,
      addAlarmLabels,
      viewDimension
    ]
  );

  // Update column/stage styles based on performance metric
  const updateColumnStyles = useCallback(
    (graph: any, performanceMetric: string) => {
      // Find the table node
      const tableNode = graph.getChildren(null).first();
      if (!tableNode) return;

      const table = ITable.getTable(tableNode) as Table;
      if (!table) return;

      // Update column group nodes (which represent stages)
      for (const node of graph.nodes) {
        if (node.tag && node.tag.type === 'COLUMN_GROUP' && node.tag.percentage) {
          // Get column and label from node tag
          const column = node.tag.column;
          const columnLabel = node.tag.columnLabel;

        if (column && columnLabel) {
          const percentageObj = node.tag.percentage;
          const displayPercentage = percentageObj.getPercentageByMetric(performanceMetric);
          const heatMapColor = getHeatMapColorFromPercentage(displayPercentage);
          
          // Update column label text with the current percentage on a new line
          const stageName = columnLabel.tag?.stageName || node.tag.stage;
          const percentageText = displayPercentage > 0 ? `\n${Math.round(displayPercentage)}%` : '';
          table.setLabelText(columnLabel, stageName + percentageText);
          
          // Only apply heatmapping if not using Health or Time metric
          if (performanceMetric !== 'Health' && performanceMetric !== 'Time') {
            // Update column background based on status - only if heatMapColor is not null
            if (heatMapColor === 'BAD') {
              const columnStyle = new NodeStyleStripeStyleAdapter(
                new ShapeNodeStyle({
                  fill: '#f5879133',
                  stroke: 'transparent'
                })
              );
              table.setStyle(column, columnStyle);

                // Update column header label style
                const headerStyle = new NodeStyleLabelStyleAdapter({
                  nodeStyle: new ShapeNodeStyle({
                    fill: '#F5D6DF',
                    stroke: 'transparent'
                  }),
                  labelStyle: new LabelStyle({
                    textFill: 'black',
                    horizontalTextAlignment: 'center',
                    verticalTextAlignment: 'center',
                    font: new Font({
                      fontFamily: 'Segoe UI',
                      fontSize: 28,
                      fontWeight: 'normal'
                    })
                  })
                });
                table.setStyle(columnLabel, headerStyle);
              } else {
                // Reset to transparent/default style for non-BAD status
                const columnStyle = new NodeStyleStripeStyleAdapter(
                  new ShapeNodeStyle({
                    fill: 'transparent',
                    stroke: 'transparent'
                  })
                );
                table.setStyle(column, columnStyle);

                // Reset header to default style
                table.setStyle(columnLabel, columnHeaderStyle);
              }
            } else {
              // Reset to default style when Health or Time metric is selected
              const columnStyle = new NodeStyleStripeStyleAdapter(
                new ShapeNodeStyle({
                  fill: 'transparent',
                  stroke: 'transparent'
                })
              );
              table.setStyle(column, columnStyle);

              // Reset header to default style
              table.setStyle(columnLabel, columnHeaderStyle);
            }
          }
        }
      }
    },
    [getHeatMapColorFromPercentage]
  );

  // Calculate percentage metrics for group nodes (capability, team, app group) from their child nodes
  const calculateCapabilityPercentages = useCallback(() => {
    if (!fullGraph) return;

    // Use the full graph but filter nodes based on their products
    // This is more reliable than trying to use the filtered view's hierarchy
    const shouldIncludeNode = (graphNode: any): boolean => {
      const node = graphNode as INode;

      // If no product filtering is active, include all nodes
      if (shownProducts.size === products.length) {
        return true;
      }

      // For PRODUCT nodes, check if they're in shownProducts
      if (node.tag?.type === 'PRODUCT') {
        return shownProducts.has(node.tag.Name);
      }

      // For LEAF nodes, check if they have edges with products in shownProducts
      if (node.tag?.type === 'LEAF') {
        return fullGraph
          .edgesAt(node)
          .some((edge) => edge.tag && edge.tag.product && shownProducts.has(edge.tag.product));
      }

      // For other nodes (TEAM, APPLICATION_GROUP, etc.), check edges
      const hasMatchingProduct = fullGraph
        .edgesAt(node)
        .some((edge) => edge.tag && edge.tag.product && shownProducts.has(edge.tag.product));

      if (hasMatchingProduct) return true;

      // For group nodes, check if any children have matching products
      if (fullGraph.isGroupNode(node)) {
        return Array.from(fullGraph.getChildren(node)).some((child) => shouldIncludeNode(child));
      }

      return false;
    };

    calculateGroupNodePercentages(new YFilesGraphAdapter(fullGraph), shouldIncludeNode);

    // Update the GraphData stages with calculated percentages from the graph nodes
    // This ensures the ValueStreamPerformanceChart shows the correct calculated values
    for (const node of fullGraph.nodes) {
      if (node.tag && node.tag.type === 'COLUMN_GROUP' && node.tag.percentage) {
        const stageName = node.tag.stage;
        const stageData = graphData.stages.find((s) => s.Name === stageName);
        if (stageData) {
          stageData.PercentageMetrics = node.tag.percentage;
        }
      }
    }

    if (typeof onHealthSnapshot === 'function') {
      const stageItems: Array<{ id: string; name: string; health: number }> = [];
      const capabilityItems: Array<{ id: string; name: string; health: number }> = [];
      for (const node of fullGraph.nodes) {
        if (node.tag && node.tag.percentage) {
          if (node.tag.type === 'COLUMN_GROUP') {
            const stageName = node.tag.stage;
            stageItems.push({
              id: stageName,
              name: stageName,
              health: node.tag.percentage.HealthPercentage
            });
          } else if (node.tag.type === 'CAPABILITY') {
            const capName = node.tag.Name || node.tag.name || 'Capability';
            const stageName = node.tag.stage || '';
            capabilityItems.push({
              id: `${stageName}:${capName}`,
              name: capName,
              health: node.tag.percentage.HealthPercentage
            });
          }
        }
      }
      onHealthSnapshot({
        stages: stageItems,
        capabilities: capabilityItems,
        timestamp: Date.now()
      });
    }
  }, [fullGraph, shownProducts, products, graphData]);

  // Function to update node styles based on selected performance metric
  const updateNodeStyles = useCallback(
    (performanceMetric: string) => {
      if (!graphComponentInstance) return;

      const graph = graphComponentInstance.graph;
      if (!graph) return;

      // Update all nodes in the graph
      for (const node of graph.nodes) {
        if (node.tag && node.tag.percentage) {
          if (node.tag.type === 'LEAF') {
            updateLeafNode(graph, node, performanceMetric);
          } else {
            updateGroupNode(graph, node, performanceMetric);
          }
        }
      }

      // Update column/stage styles
      updateColumnStyles(graph, performanceMetric);

      // Update edge styles based on performance metric
      // Enable flow animation only for Time metric
      const shouldAnimate = performanceMetric === 'Time';
      const animator = animatorRef.current;
      const dotPixelsPerSecond = dotPixelsPerSecondRef.current;

      if (animator && products.length > 0) {
        const maxRevenue = Math.max(...products.map((p) => parseInt(p.Revenue)));

        for (const edge of graph.edges) {
          if (edge.tag && edge.tag.product) {
            const productName = edge.tag.product;
            const product = products.find((p) => p.Name === productName);

            if (product) {
              const edgeType = edge.tag.type;
              // For Time metric: animate inter-stage flows
              // For other metrics: no animation
              const useAnimation = shouldAnimate && edgeType === 'INTER_STAGE_FLOW';

              const newStyle = useAnimation
                ? getAnimatedProductEdgeStyle(product, products, maxRevenue, dotPixelsPerSecond)
                : getProductEdgeStyle(product, products, maxRevenue);

              graph.setStyle(edge, newStyle);
            }
          }
        }
      }

      graphComponentInstance.invalidate();
    },
    [graphComponentInstance, updateLeafNode, updateGroupNode, updateColumnStyles, products]
  );

  const runLayoutAndUpdate = useCallback(
    async (
      gc: GraphComponent,
      incremental = false,
      source = 'unknown',
      panTo: INode | undefined = undefined
    ) => {
      if (!gc) return;

      // Clear any pending layout operation
      if (layoutTimeoutRef.current !== null) {
        window.clearTimeout(layoutTimeoutRef.current);
        layoutTimeoutRef.current = null;
        logLayoutOperation(
          `runLayoutAndUpdate from ${source}`,
          `canceled pending layout, scheduling new one`
        );
      } else {
        logLayoutOperation(`runLayoutAndUpdate from ${source}`, `morph=${incremental}`);
      }

      gc.highlightIndicatorManager.items.clear();

      layoutTimeoutRef.current = window.setTimeout(async () => {
        try {
          const startTime = performance.now();
          logLayoutOperation(
            `runLayoutAndUpdate from ${source}`,
            `executing layout, morph=${incremental}`
          );
          await runLayout(gc, stages, incremental, viewType);
          updatePortVisuals(gc.graph);
          if (gc.graphModelManager instanceof ValueStreamGraphModelManager) {
            gc.graphModelManager.addBackgroundVisuals();
            gc.graphModelManager.updateZOrder();
          }
          updateNodeZOrder();

          updateNodeStyles(selectedPerformanceMetric);
          if (panTo) {
            await gc.zoomToAnimated(gc.zoom, panTo.layout.center);
          } else {
            await fitGraphBounds();
          }
          const duration = performance.now() - startTime;
          logLayoutOperation(
            `runLayoutAndUpdate from ${source}`,
            `layout completed in ${duration.toFixed(0)}ms`
          );
        } catch (error) {
          console.error('Error during layout operation:', error);
        } finally {
          layoutTimeoutRef.current = null;
        }
      }, 50);
    },
    [stages, viewType, selectedPerformanceMetric, updateNodeZOrder, updateNodeStyles]
  );

  // Sync performance metric to URL when it changes (only after initialization)
  useEffect(() => {
    if (!isMetricInitialized) return;

    // Read current URL params at update time to avoid race conditions
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('performanceMetric', selectedPerformanceMetric);
    setSearchParams(currentParams, { replace: true });

    // Notify parent of metric change
    onPerformanceMetricChange?.(selectedPerformanceMetric);
  }, [selectedPerformanceMetric, isMetricInitialized, setSearchParams, onPerformanceMetricChange]);

  // Apply performance metric from URL on mount
  useEffect(() => {
    if (isMetricInitialized) return;

    const urlMetric = getInitialPerformanceMetric();
    setSelectedPerformanceMetric(urlMetric);
    setIsMetricInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isProductsInitialized || products.length === 0) return;

    const syncProductsToUrl = async () => {
      // Read current URL params at update time to avoid race conditions
      const currentParams = new URLSearchParams(window.location.search);
      const shownProductsArray = Array.from(shownProducts);

      // Only add products param if not all products are shown
      if (shownProductsArray.length === products.length) {
        currentParams.delete('products');
      } else {
        const compressed = await compressStringArray(shownProductsArray);
        currentParams.set('products', compressed);
      }

      setSearchParams(currentParams, { replace: true });
    };

    syncProductsToUrl();
  }, [shownProducts, isProductsInitialized, products.length, setSearchParams]);

  useEffect(() => {
    updateNodeStyles(selectedPerformanceMetric);
  }, [selectedPerformanceMetric, updateNodeStyles]);

  // Sync updated Percentage values from graphData into the internal yFiles graph nodes
  // This fires when the time slider changes (graphData prop gets new Percentage values)
  useEffect(() => {
    if (!fullGraph || !graphComponentInstance) return;

    // Build lookup maps from graphData items to their updated Percentage
    const capabilityPercentages = new Map<string, PercentageMetrics>();
    graphData.capabilities?.forEach((cap) => {
      if (cap.Name && cap.Percentage) capabilityPercentages.set(cap.Name, cap.Percentage);
    });

    const teamPercentages = new Map<string, PercentageMetrics>();
    graphData.teams?.forEach((t) => {
      if (t.team && t.Percentage) teamPercentages.set(t.team, t.Percentage);
    });

    const appGroupPercentages = new Map<string, PercentageMetrics>();
    graphData.applicationGroups?.forEach((ag) => {
      if (ag.ApplicationGroup && ag.Percentage)
        appGroupPercentages.set(ag.ApplicationGroup, ag.Percentage);
    });

    // If nothing has Percentage data, skip
    if (
      capabilityPercentages.size === 0 &&
      teamPercentages.size === 0 &&
      appGroupPercentages.size === 0
    )
      return;

    // Update internal graph node tags with new Percentage values
    let anyUpdated = false;
    for (const node of fullGraph.nodes) {
      if (!node.tag) continue;
      const type = node.tag.type;
      let newPct: PercentageMetrics | undefined;

      if (type === 'CAPABILITY') {
        newPct = capabilityPercentages.get(node.tag.Name);
      } else if (type === 'TEAM') {
        newPct = teamPercentages.get(node.tag.team);
      } else if (type === 'APPLICATION_GROUP') {
        newPct = appGroupPercentages.get(node.tag.Name);
      }

      if (newPct && newPct !== node.tag.percentage) {
        node.tag.percentage = newPct;
        anyUpdated = true;
      }
    }

    if (!anyUpdated) return;

    // Recalculate group node percentages from updated leaf/child data
    calculateGroupNodePercentages(new YFilesGraphAdapter(fullGraph));

    // Fire onHealthSnapshot with recalculated values
    if (typeof onHealthSnapshot === 'function') {
      const stageItems: Array<{ id: string; name: string; health: number }> = [];
      const capabilityItems: Array<{ id: string; name: string; health: number }> = [];
      for (const node of fullGraph.nodes) {
        if (node.tag && node.tag.percentage) {
          if (node.tag.type === 'COLUMN_GROUP') {
            const stageName = node.tag.stage;
            stageItems.push({
              id: stageName,
              name: stageName,
              health: node.tag.percentage.HealthPercentage
            });
          } else if (node.tag.type === 'CAPABILITY') {
            const capName = node.tag.Name || node.tag.name || 'Capability';
            const stageName = node.tag.stage || '';
            capabilityItems.push({
              id: `${stageName}:${capName}`,
              name: capName,
              health: node.tag.percentage.HealthPercentage
            });
          }
        }
      }
      onHealthSnapshot({
        stages: stageItems,
        capabilities: capabilityItems,
        timestamp: Date.now()
      });
    }

    // Re-apply visual styles with updated percentages
    updateNodeStyles(selectedPerformanceMetric);
    updatePortVisuals(graphComponentInstance.graph);
    graphComponentInstance.invalidate();
  }, [
    fullGraph,
    graphComponentInstance,
    graphData,
    onHealthSnapshot,
    updateNodeStyles,
    selectedPerformanceMetric
  ]);

  useEffect(() => {
    if (!graphComponentInstance) return;

    const expandCollapseListener = async (
      args: InputModeItemEventArgs<INode>,
      sender: NavigationInputMode
    ) => {
      if (!graphComponentInstance) return;
      const node = args.item;
      const tag = (sender as any)?.item?.tag ?? {};
      if (!node) return;
      const nodeType = tag?.type as string | undefined;
      const nodeId = tag?.nodeId ?? 'unknown';

      let shouldRunLayout = true;

      if (shouldRunLayout) {
        logLayoutOperation(
          'expandCollapseListener',
          `RUN layout (collapsed ${nodeType}) nodeId=${nodeId}`
        );
        await runLayoutAndUpdate(
          graphComponentInstance,
          true,
          'expandCollapseListener:conditional',
          node
        );
        updateNodeStyles(selectedPerformanceMetric);
        graphComponentInstance.invalidate();
      }
    };

    const navigationInputMode = (graphComponentInstance.inputMode as GraphViewerInputMode)
      .navigationInputMode;

    navigationInputMode.addEventListener('group-collapsed', expandCollapseListener);
    navigationInputMode.addEventListener('group-expanded', expandCollapseListener);

    return () => {
      navigationInputMode.removeEventListener('group-collapsed', expandCollapseListener);
      navigationInputMode.removeEventListener('group-expanded', expandCollapseListener);
    };
  }, [graphComponentInstance, viewType, runLayoutAndUpdate, updateNodeStyles, selectedPerformanceMetric]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (layoutTimeoutRef.current !== null) {
        window.clearTimeout(layoutTimeoutRef.current);
      }
    };
  }, []);

  const handleToggleProduct = useCallback((productName: string) => {
    setShownProducts((prevShown) => {
      const newShown = new Set(prevShown);
      if (newShown.has(productName)) {
        newShown.delete(productName);
      } else {
        newShown.add(productName);
      }
      return newShown;
    });
  }, []);

  useEffect(() => {
    if (!foldingView || !graphComponentInstance || isLoading || stages.length === 0) return;

    // Check if shownProducts has actually changed to prevent infinite loops
    const currentProducts = Array.from(shownProducts).sort().join(',');
    const prevProducts = Array.from(prevShownProductsRef.current).sort().join(',');

    if (currentProducts === prevProducts) {
      logLayoutOperation('shownProducts useEffect', 'SKIPPED - no actual products change');
      return;
    }

    // Update the ref with current products
    prevShownProductsRef.current = new Set(shownProducts);

    logLayoutOperation('shownProducts useEffect', `products changed: ${currentProducts}`);

    try {
      // Call enableFilteringAndFolding inside the effect instead of depending on it
      const view = enableFilteringAndFolding();

      const center = graphComponentInstance.viewport.center;
      const zoom = graphComponentInstance.zoom;

      graphComponentInstance.graph = view.graph;
      setFoldingView(view);

      graphComponentInstance.center = center;
      graphComponentInstance.zoom = zoom;

      void fitGraphBounds();

      // Recalculate capability percentages based on visible child nodes
      calculateCapabilityPercentages();

      runLayoutAndUpdate(graphComponentInstance, false, 'shownProducts useEffect');

      updateNodeStyles(selectedPerformanceMetric);
    } catch (error) {
      console.error('Error updating filtered graph:', error);
    }
  }, [
    shownProducts,
    stages,
    graphComponentInstance,
    isLoading,
    foldingView,
    viewType,
    insetTop,
    insetRight,
    insetBottom,
    insetLeft,
    enableFilteringAndFolding,
    calculateCapabilityPercentages,
    selectedPerformanceMetric,
    runLayoutAndUpdate,
    updateNodeStyles
  ]);

  const handleViewTypeChange = useCallback((newViewType: 'FLOW' | 'TABULAR') => {
    setViewType(newViewType);
  }, []);

  useEffect(() => {
    if (!foldingView || !graphComponentInstance || isLoading || stages.length === 0) return;

    // Skip if viewType hasn't actually changed
    const didViewTypeChange = prevViewTypeRef.current !== viewType;
    prevViewTypeRef.current = viewType;

    if (!didViewTypeChange) {
      logLayoutOperation('viewType useEffect', 'SKIPPED - no actual viewType change');
      return;
    }

    logLayoutOperation(
      'viewType useEffect',
      `changing from ${prevViewTypeRef.current} to ${viewType}`
    );

    try {
      const expandedIds = captureExpandedState();
      expandedNodesRef.current = expandedIds;

      // Call enableFilteringAndFolding inside the effect instead of depending on it
      const view = enableFilteringAndFolding();

      const center = graphComponentInstance.viewport.center;
      const zoom = graphComponentInstance.zoom;

      graphComponentInstance.graph = view.graph;
      setFoldingView(view);

      graphComponentInstance.center = center;
      graphComponentInstance.zoom = zoom;

      // For view type changes, we need to run the layout immediately in sequence
      // to ensure proper expansion state handling
      const executeViewTypeChange = async () => {
        try {
          setIsLoading(true);

          logLayoutOperation('viewType useEffect', 'pass 1 - initial placement');
          await runLayout(graphComponentInstance, stages, false, viewType);

          logLayoutOperation('viewType useEffect', 'restoring expanded state');
          restoreExpandedState(expandedIds, view);

          logLayoutOperation('viewType useEffect', 'pass 2 - after expansion');
          await runLayout(graphComponentInstance, stages, false, viewType);

          updateNodeStyles(selectedPerformanceMetric);
        } catch (layoutError) {
          console.error('Error during layout update:', layoutError);
        } finally {
          setIsLoading(false);
        }
      };
      executeViewTypeChange();
    } catch (error) {
      console.error('Error updating filtered graph for view type change:', error);
    }
  }, [
    viewType,
    stages,
    graphComponentInstance,
    isLoading,
    foldingView,
    captureExpandedState,
    restoreExpandedState,
    insetTop,
    insetRight,
    insetBottom,
    insetLeft
  ]);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);

      if (!graphComponentInstance || !foldingView) return;

      const graph = graphComponentInstance.graph;

      setHighlightedNodes([]);
      graphComponentInstance.highlightIndicatorManager.items.clear();

      if (!term.trim()) {
        void fitGraphBounds();
        return;
      }

      const matchingNodes: INode[] = [];
      graph.nodes.forEach((node: INode) => {
        const nodeTag = node.tag;
        const name = nodeTag?.name || nodeTag?.Name;
        if (
          nodeTag &&
          typeof name === 'string' &&
          name.toLowerCase().includes(term.toLowerCase())
        ) {
          matchingNodes.push(node);
        }
      });

      matchingNodes.forEach((node: INode) => {
        graphComponentInstance.highlightIndicatorManager.items.add(node);
      });

      setHighlightedNodes(matchingNodes);

      if (matchingNodes.length > 0) {
        const zoomToRect = new MutableRectangle();
        matchingNodes.forEach((node: INode) => {
          zoomToRect.add(node.layout.toRect());
        });
        void graphComponentInstance.zoomToAnimated(zoomToRect.toRect().getEnlarged(50));
      }
    },
    [
      graphComponentInstance,
      foldingView,
      highlightedNodes,
      insetLeft,
      insetTop,
      insetRight,
      insetBottom
    ]
  );

  // Zoom specifically to stage headers (COLUMN_GROUP group nodes) without touching search state/highlights
  const handleZoomStageHeader = useCallback(
    (stageTerm: string) => {
      if (!graphComponentInstance || !foldingView) return;

      const graph = graphComponentInstance.graph;

      // Do not change searchTerm or existing highlights to avoid interfering with search UX
      const matchingStages: INode[] = [];
      graph.nodes.forEach((node: INode) => {
        const tag = node.tag;
        if (tag && tag.type === 'COLUMN_GROUP') {
          const stageName = tag.stage || tag.stageName || tag.name || tag.Name;
          if (
            typeof stageName === 'string' &&
            stageName.toLowerCase().includes(stageTerm.toLowerCase())
          ) {
            matchingStages.push(node);
          }
        }
      });

      if (matchingStages.length > 0) {
        const zoomToRect = new MutableRectangle();
        matchingStages.forEach((node: INode) => {
          zoomToRect.add(node.layout.toRect());
        });
        void graphComponentInstance.zoomToAnimated(zoomToRect.toRect().getEnlarged(100));
      }
    },
    [graphComponentInstance, foldingView]
  );

  // Expose search API to parent when available
  useEffect(() => {
    if (typeof onSearchApiReady === 'function') {
      onSearchApiReady({ search: handleSearch, zoomStageHeader: handleZoomStageHeader });
    }
  }, [onSearchApiReady, handleSearch, handleZoomStageHeader]);

  const handleZoomIn = useCallback(() => {
    if (graphComponentInstance) {
      graphComponentInstance.zoom = graphComponentInstance.zoom * 1.2;
    }
  }, [graphComponentInstance]);

  const handleZoomOut = useCallback(() => {
    if (graphComponentInstance) {
      graphComponentInstance.zoom = graphComponentInstance.zoom / 1.2;
    }
  }, [graphComponentInstance]);

  const handleFitGraph = useCallback(() => {
    if (graphComponentInstance) {
      void fitGraphBounds();
    }
  }, [graphComponentInstance, insetLeft, insetTop, insetRight, insetBottom]);

  const handleExportSvg = useCallback(() => {
    if (graphComponentInstance) {
      import('./diagram-export').then((module) => {
        module.exportDiagram(graphComponentInstance, 'svg');
      });
    }
  }, [graphComponentInstance]);

  const handleExportPng = useCallback(() => {
    if (graphComponentInstance) {
      import('./diagram-export').then((module) => {
        module.exportDiagram(graphComponentInstance, 'png');
      });
    }
  }, [graphComponentInstance]);

  const handleExportPdf = useCallback(() => {
    if (graphComponentInstance) {
      import('./diagram-export').then((module) => {
        module.exportDiagram(graphComponentInstance, 'pdf');
      });
    }
  }, [graphComponentInstance]);

  const isClickOnGroupChevron = (node: INode, clickX: number, clickY: number): boolean => {
    // Only GroupNodeStyle nodes render the expand/collapse chevron.
    if (!(node.style instanceof GroupNodeStyle)) {
      return false;
    }

    const style = node.style as GroupNodeStyle;
    const iconSize = style.iconSize;
    const iconOffset = style.iconOffset;
    const layout = node.layout;
    // Slightly larger hit target than the visual icon to avoid precision-clicking.
    const pad = 6;

    // Candidate #1: top-left icon placement.
    const tlLeft = layout.x + iconOffset - pad;
    const tlTop = layout.y + iconOffset - pad;
    const tlRight = layout.x + iconOffset + iconSize + pad;
    const tlBottom = layout.y + iconOffset + iconSize + pad;

    // Candidate #2: top-right icon placement (used in some runtime style variants).
    const trLeft = layout.x + layout.width - iconOffset - iconSize - pad;
    const trTop = layout.y + iconOffset - pad;
    const trRight = layout.x + layout.width - iconOffset + pad;
    const trBottom = layout.y + iconOffset + iconSize + pad;

    // Suppress metric dialog if click lands in either chevron hit box.
    const inTopLeft =
      clickX >= tlLeft && clickX <= tlRight && clickY >= tlTop && clickY <= tlBottom;
    const inTopRight =
      clickX >= trLeft && clickX <= trRight && clickY >= trTop && clickY <= trBottom;

    return inTopLeft || inTopRight;
  };

  const handleItemSingleClick = useCallback(
    (evt: any, graphComponentInstance: GraphComponent) => {
      if (!graphComponentInstance || (evt.pointerButtons & PointerButtons.MOUSE_LEFT) === 0) {
        return;
      }

      const inputMode = graphComponentInstance.inputMode as GraphViewerInputMode;
      const item = inputMode
        .findItems(evt.context, evt.location, [GraphItemTypes.NODE | GraphItemTypes.EDGE])
        .at(0);

      // If the click landed on a group node's chevron icon area, skip metrics
      if (
        item &&
        item instanceof INode &&
        isClickOnGroupChevron(item, evt.location.x, evt.location.y)
      ) {
        // Chevron click should only toggle expand/collapse, not open metrics.
        return;
      }

      if (onItemSingleClick) {
        onItemSingleClick(item, graphComponentInstance);
      }
    },
    [onItemSingleClick]
  );

  const handleItemDoubleClick = useCallback(
    (evt: any, graphComponentInstance: GraphComponent) => {
      if (!graphComponentInstance || (evt.pointerButtons & PointerButtons.MOUSE_LEFT) === 0) {
        return;
      }

      const inputMode = graphComponentInstance.inputMode as GraphViewerInputMode;
      const item = inputMode
        .findItems(evt.context, evt.location, [GraphItemTypes.NODE | GraphItemTypes.EDGE])
        .at(0);

      if (!item || !(item instanceof INode)) {
        return;
      }

      const clickedNode = item;
      const graph = graphComponentInstance.graph;

      if (graph.isGroupNode(clickedNode) || ITable.getTable(clickedNode)) {
        return;
      }

      const foldingView = graph.foldingView as IFoldingView;
      const masterGraph = foldingView.manager.masterGraph;
      const masterItem = foldingView.getMasterItem(clickedNode);

      if (!masterItem) return;

      if (masterItem.tag?.type === 'PRODUCT') {
        const newShownProducts = new Set([masterItem.tag.Name]);
        setShownProducts(newShownProducts);
        setIsFilterActive(false); // This is a product filter, not a flow filter
      } else {
        const products = getProductsForNode(masterGraph, masterItem);
        const flowNodesList = getFlowNodes(masterItem, masterGraph, products);
        const newFlowNodes = new Set<INode>();
        const newFlowEdges = new Set<IEdge>();

        getFlowItems(flowNodesList, masterGraph, products, newFlowEdges, newFlowNodes);

        const allNodesToShow = new Set<INode>(newFlowNodes);
        for (const node of newFlowNodes) {
          let parent = masterGraph.getParent(node);
          while (parent) {
            allNodesToShow.add(parent);
            parent = masterGraph.getParent(parent);
          }
        }

        expandedNodesRef.current = captureExpandedState();

        setFlowNodesToFilterFor(Array.from(allNodesToShow));
        setFlowEdgesToFilterFor(Array.from(newFlowEdges));

        setIsFilterActive(true);
      }
    },
    [graphComponentInstance, stages, viewType]
  );

  useEffect(() => {
    if (!graphComponentInstance) return;

    const filteredGraphWrapper = graphComponentInstance.graph.foldingView!.manager
      .masterGraph as FilteredGraphWrapper;
    filteredGraphWrapper.nodePredicate = nodePredicate;
    filteredGraphWrapper.edgePredicate = edgePredicate;
    filteredGraphWrapper.nodePredicateChanged();
    filteredGraphWrapper.edgePredicateChanged();
    logLayoutOperation('filteringForflow', 'filtering for flow');
    void runLayoutAndUpdate(graphComponentInstance, false, 'handleResetFilter');
  }, [nodePredicate, edgePredicate]);

  const handleResetFilter = useCallback(() => {
    setFlowNodesToFilterFor([]);
    setFlowEdgesToFilterFor([]);
    setIsFilterActive(false);

    if (products.length > 0) {
      const productSet = new Set<string>();
      products.forEach((product) => productSet.add(product.Name));
      // Update both state and ref to avoid triggering the effect
      setShownProducts(productSet);
      prevShownProductsRef.current = new Set(productSet);
    }

    handleToggleProduct('___INVALID___'); // TODO fix filter clearing functionality

    if (graphComponentInstance) {
      logLayoutOperation('handleResetFilter', 'resetting filter');
      runLayoutAndUpdate(graphComponentInstance, false, 'handleResetFilter');
    }
  }, [products, graphComponentInstance, stages, viewType]);

  const handleViewDimensionChange = useCallback(
    async (dimension: '2D' | '3D') => {
      if (!graphComponentInstance || !foldingView) return;

      const fullGraph = (foldingView.manager.masterGraph as FilteredGraphWrapper).wrappedGraph;
      const masterGraph = foldingView.manager.masterGraph;
      if (!fullGraph) return;

      if (dimension === '2D') {
        setViewDimension('2D');
        const nodes = fullGraph.nodes;
        for (const node of nodes) {
          unwrap3DStyle(fullGraph, node);
          node.labels.forEach((label: ILabel) => unwrap3DStyle(fullGraph, label));
        }
        for (const node of masterGraph.nodes) {
          unwrap3DStyle(masterGraph, node);
          node.labels.forEach((label: ILabel) => unwrap3DStyle(masterGraph, label));
        }
        // Also unwrap labels in the folding view itself.
        // Collapsed groups become folder nodes whose labels are *copies* (not proxies)
        // of the master-graph labels, so the loops above don't reach them.
        const viewGraph = foldingView.graph;
        for (const node of viewGraph.nodes) {
          node.labels.forEach((label: ILabel) => unwrap3DStyle(viewGraph, label));
        }

        let fromRotation: number;
        let toRotation: number;
        let direction: 1 | -1;

        if (orientation.rotation <= 0 && orientation.rotation >= -180) {
          fromRotation = degreeToRadians(normalizeRotation(orientation.rotation));
          toRotation = degreeToRadians(360);
          direction = 1;
        } else {
          fromRotation = degreeToRadians(normalizeRotation(orientation.rotation));
          toRotation = degreeToRadians(rotation2D);
          direction = -1;
        }

        const fromInclination = degreeToRadians(orientation.inclination);
        const toInclination = degreeToRadians(inclination2D);
        await animateProjection(
          fromRotation,
          toRotation,
          fromInclination,
          toInclination,
          direction
        );
      } else if (dimension === '3D') {
        setViewDimension('3D');
        for (const node of fullGraph.nodes) {
          if (isSupportedStyle(node)) {
            wrapInto3DStyle(fullGraph, node);
            node.labels.forEach((label: ILabel) => wrapInto3DStyle(fullGraph, label));
          }
        }
        for (const node of masterGraph.nodes) {
          if (isSupportedStyle(node)) {
            wrapInto3DStyle(masterGraph, node);
            node.labels.forEach((label: ILabel) => wrapInto3DStyle(masterGraph, label));
          }
        }
        // Also wrap labels in the folding view itself.
        // Collapsed groups become folder nodes whose labels are *copies* (not proxies)
        // of the master-graph labels, so the loops above don't reach them.
        // wrapInto3DStyle guards against double-wrapping labels that were already
        // covered by the fullGraph/masterGraph loops above.
        const viewGraph = foldingView.graph;
        for (const node of viewGraph.nodes) {
          node.labels.forEach((label: ILabel) => wrapInto3DStyle(viewGraph, label));
        }

        const fromRotation = degreeToRadians(rotation2D);
        let toRotation: number;
        let direction: 1 | -1;

        if (orientation.rotation >= 0 && orientation.rotation <= 180) {
          toRotation = degreeToRadians(normalizeRotation(orientation.rotation));
          direction = 1;
        } else {
          const targetRotation = -(360 - orientation.rotation);
          toRotation = degreeToRadians(normalizeRotation(targetRotation));
          direction = -1;
        }

        const fromInclination = degreeToRadians(inclination2D);
        const toInclination = degreeToRadians(orientation.inclination);
        await animateProjection(
          fromRotation,
          toRotation,
          fromInclination,
          toInclination,
          direction
        );
      }
    },
    [graphComponentInstance, foldingView, animateProjection, orientation, setViewDimension]
  );

  useEffect(() => {
    if (graphComponentInstance && viewDimension === '3D') {
      (graphComponentInstance as any).orientation = {
        rotation: normalizeRotation(orientation.rotation),
        inclination: orientation.inclination
      };

      const rotation = degreeToRadians(orientation.rotation);
      const inclination = degreeToRadians(orientation.inclination);

      const rotationMatrix = Matrix.createRotateInstance(rotation);
      const inclinationMatrix = new Matrix(1, 0, 0, Math.sin(inclination), 0, 0);
      rotationMatrix.multiply(inclinationMatrix, MatrixOrder.APPEND);

      graphComponentInstance.projection = rotationMatrix;
      updateNodeZOrder();
      graphComponentInstance.invalidate();
    }
  }, [orientation, graphComponentInstance, viewDimension, updateNodeZOrder]);

  useEffect(() => {
    if (!graphComponentInstance) {
      return;
    }

    const graph = graphComponentInstance.graph;
    const parameter = alarmHaloLabelLayoutParameter;

    for (const label of graph.labels) {
      if (label.tag?.type === 'ALARM_HALO' && label.layoutParameter !== parameter) {
        graph.setLabelLayoutParameter(label, parameter);
      }
    }

    const masterGraph = foldingView?.manager?.masterGraph;
    if (masterGraph) {
      for (const label of masterGraph.labels) {
        if (label.tag?.type === 'ALARM_HALO' && label.layoutParameter !== parameter) {
          masterGraph.setLabelLayoutParameter(label, parameter);
        }
      }
    }

    graphComponentInstance.invalidate();
  }, [graphComponentInstance, foldingView]);

  const normalizeRotation = (rotation: number) => (rotation + 360) % 360;
  const degreeToRadians = (degree: number) => (degree * Math.PI) / 180;

  return (
    <div
      style={
        {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100vh',
          overflow: 'hidden',
          position: 'relative'
        } as React.CSSProperties
      }
    >
      {portalTargetEl &&
        createPortal(
          <div>
            <div className="nlc-collapse">
              <div className="vertical-collapse">
                <ProgressiveCollapse
                  ref={progressiveCollapseRef}
                  foldingView={foldingView}
                  graphComponentInstance={graphComponentInstance}
                  runLayoutAndUpdate={(gc, morph) =>
                    runLayoutAndUpdate(gc, morph, 'ProgressiveCollapse')
                  }
                  parentInitialized={isProductsInitialized}
                />
                <br />
                {!isFilterActive && (
                  <ProductFilter
                    products={products}
                    shownProducts={shownProducts}
                    onToggleProduct={handleToggleProduct}
                  />
                )}

                <br />
                <div className="w-full">
                  <div className="flex items-center px-2 py-1 font-medium text-sm text-gray-900 border-b border-gray-200 mb-1">
                    <span>Performance</span>
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                    <label
                      htmlFor="performance-1"
                      title="Health"
                      className={cn(
                        'flex items-center w-full px-2 py-1 font-medium rounded-md cursor-pointer',
                        'text-xs',
                        selectedPerformanceMetric === 'Health'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      <input
                        type="radio"
                        id="performance-1"
                        name="performance"
                        checked={selectedPerformanceMetric === 'Health'}
                        onChange={() => setSelectedPerformanceMetric('Health')}
                        className="mr-2"
                      />
                      <Activity className="w-4 h-4 mr-2" />
                      Health
                    </label>
                    <label
                      htmlFor="performance-2"
                      title="Time"
                      className={cn(
                        'flex items-center w-full px-2 py-1 font-medium rounded-md cursor-pointer',
                        'text-xs',
                        selectedPerformanceMetric === 'Time'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      <input
                        type="radio"
                        id="performance-2"
                        name="performance"
                        checked={selectedPerformanceMetric === 'Time'}
                        onChange={() => setSelectedPerformanceMetric('Time')}
                        className="mr-2"
                      />
                      <Activity className="w-4 h-4 mr-2" />
                      Time
                    </label>
                    <label
                      htmlFor="performance-3"
                      title="Volume"
                      className={cn(
                        'flex items-center w-full px-2 py-1 font-medium rounded-md cursor-pointer',
                        'text-xs',
                        selectedPerformanceMetric === 'Volume'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      <input
                        type="radio"
                        id="performance-3"
                        name="performance"
                        checked={selectedPerformanceMetric === 'Volume'}
                        onChange={() => setSelectedPerformanceMetric('Volume')}
                        className="mr-2"
                      />
                      <Activity className="w-4 h-4 mr-2" />
                      Volume
                    </label>
                    <label
                      htmlFor="performance-4"
                      title="Quality"
                      className={cn(
                        'flex items-center w-full px-2 py-1 font-medium rounded-md cursor-pointer',
                        'text-xs',
                        selectedPerformanceMetric === 'Quality'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      <input
                        type="radio"
                        id="performance-4"
                        name="performance"
                        checked={selectedPerformanceMetric === 'Quality'}
                        onChange={() => setSelectedPerformanceMetric('Quality')}
                        className="mr-2"
                      />
                      <Activity className="w-4 h-4 mr-2" />
                      Quality
                    </label>
                    <label
                      htmlFor="performance-5"
                      title="Sentiment"
                      className={cn(
                        'flex items-center w-full px-2 py-1 font-medium rounded-md cursor-pointer',
                        'text-xs',
                        selectedPerformanceMetric === 'Sentiment'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      <input
                        type="radio"
                        id="performance-5"
                        name="performance"
                        checked={selectedPerformanceMetric === 'Sentiment'}
                        onChange={() => setSelectedPerformanceMetric('Sentiment')}
                        className="mr-2"
                      />
                      <Activity className="w-4 h-4 mr-2" />
                      Sentiment
                    </label>
                    <label
                      htmlFor="performance-6"
                      title="Cost"
                      className={cn(
                        'flex items-center w-full px-2 py-1 font-medium rounded-md cursor-pointer',
                        'text-xs',
                        selectedPerformanceMetric === 'Cost'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      <input
                        type="radio"
                        id="performance-6"
                        name="performance"
                        checked={selectedPerformanceMetric === 'Cost'}
                        onChange={() => setSelectedPerformanceMetric('Cost')}
                        className="mr-2"
                      />
                      <Activity className="w-4 h-4 mr-2" />
                      Cost
                    </label>
                    <label
                      htmlFor="performance-7"
                      title="Risk"
                      className={cn(
                        'flex items-center w-full px-2 py-1 font-medium rounded-md cursor-pointer',
                        'text-xs',
                        selectedPerformanceMetric === 'Risk'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      <input
                        type="radio"
                        id="performance-7"
                        name="performance"
                        checked={selectedPerformanceMetric === 'Risk'}
                        onChange={() => setSelectedPerformanceMetric('Risk')}
                        className="mr-2"
                      />
                      <Activity className="w-4 h-4 mr-2" />
                      Risk
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          portalTargetEl
        )}
      <Toolbar
        onSearch={handleSearch}
        viewType={viewType}
        onViewTypeChange={handleViewTypeChange}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFitGraph={handleFitGraph}
        onExportSvg={handleExportSvg}
        onExportPng={handleExportPng}
        onExportPdf={handleExportPdf}
        onExpandAll={() => progressiveCollapseRef.current?.handleExpandAll()}
        onCollapseAll={() => progressiveCollapseRef.current?.handleCollapseAll()}
        onResetFilter={handleResetFilter}
        onViewDimensionChange={handleViewDimensionChange}
        viewDimension={viewDimension}
        orientation={orientation}
        setOrientation={setOrientation}
      />
      <div style={{ flexGrow: 1, position: 'relative' }}>
        <LoadingOverlay isLoading={isLoading} />
        <div ref={graphComponentRef} style={{ width: '100%', height: '100%' }} />
      </div>
    </div>
  );
};

export default OrbusStreamNavigator;
